import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, take } from 'rxjs';

import * as fromGenerated from '../_generated';
import { AuthState, authFeature } from './store/auth.reducer';

/**
 * Guard factory — mirrors the API's @AuthRoles() decorator.
 * Pass the allowed roles at route-definition time; the returned CanActivateFn
 * checks the authenticated user's role and redirects to /chats if insufficient.
 *
 * Composed after authGuard, which populates the user in the store. On a hard
 * refresh the store is empty when guards first run, so we wait for the user to
 * resolve (authGuard's getMe) before checking the role — reading it
 * synchronously would redirect an authorized user away on every page reload.
 *
 * @example
 * canActivate: [authGuard, authRoleGuard([UserRole.admin, UserRole.owner, UserRole.super_admin])]
 */
export const authRoleGuard = (
  roles: fromGenerated.UserRole[],
): CanActivateFn => {
  return () => {
    const store = inject<Store<AuthState>>(Store);
    const router = inject(Router);

    return store.select(authFeature.selectUser).pipe(
      filter((user): user is fromGenerated.UserView => user !== null),
      take(1),
      map<fromGenerated.UserView, boolean | UrlTree>((user) =>
        roles.includes(user.role) ? true : router.createUrlTree(['/chats']),
      ),
    );
  };
};
